import {
  UpdateHorizontalWorkloadScaling,
  UpdateVerticalWorkloadScaling,
  WorkloadOptimizationState,
  WorkloadRolloutBehaviorType,
} from '@cast/types';

import { PreparedWorkload } from 'features/workload-optimization';

import {
  VerticalRightsizingPolicyFormModelGeneratorParams,
  VerticalRightsizingPolicyFormModelGenerators,
  VerticalRightsizingPolicyFormModel,
  WorkloadDetailsFormModel,
  HorizontalRightsizingFormModel,
  HorizontalRightsizingFormModelV2,
} from './_types';
import { PartialWorkloadUpdateRequest } from '../../../../../hooks/mutations/workload-optimization/useUpdateWorkloadV2Mutation';
import { HpaV2Status } from '../../_hooks/useHpaV2Status';
import { Path, PolicyAutoscalerFormMode } from '../../_types';
import {
  getAntiAffinityApiValue,
  getLookbackDaysHours,
  getOptimizationThresholdFormModel,
  getOverhead,
  getRecommendationPercentile,
  getResourceLimitsCpu,
  getResourceLimitsMemory,
  isWorkloadResourceOptimized,
  secondsToStartupMetricsFormModel,
  getRestartPodsOneByOne,
  getAntiAffinity,
  getConstraintsApiValues,
  toMibOrNull,
  mapApiMetricsToFormModel,
  mapFormModelMetricsToApi,
  mapApiAutoscalerBehaviorToFormModel,
  mapFormModelAutoscalerBehaviorToApi,
  isHpaV2Unavailable,
  workloadHasHpaV2Settings,
  getStallDetectionFormModel,
  getWorkloadConstraints,
  getDelayMinutesSeconds,
} from '../../_utils';

const isDefined = (...args: any[]): boolean => {
  return args.some((arg) => arg !== undefined);
};

const apiToVerticalRightsizingPolicyFormModelFunctions: VerticalRightsizingPolicyFormModelGenerators =
  {
    applyType: ({
      workload: {
        workloadConfigV2: {
          vpaConfig: { applyType },
        },
        workloadOverrides,
      },
    }) => ({
      value: applyType === 'UNKNOWN' ? 'DEFERRED' : applyType,
      isOverridden: isDefined(workloadOverrides?.vertical?.applyType),
    }),

    rolloutBehavior: ({
      workload: {
        workloadConfigV2: { rolloutBehavior },
        workloadOverrides,
      },
    }) => ({
      restartPodsOneByOne: getRestartPodsOneByOne(rolloutBehavior),
      zeroDowntime:
        rolloutBehavior?.type === WorkloadRolloutBehaviorType.NO_DISRUPTION,
      delaySecondsEnabled: !!rolloutBehavior?.delaySeconds,
      delaySeconds: getDelayMinutesSeconds(rolloutBehavior?.delaySeconds),
      isOverridden:
        isDefined(workloadOverrides?.rolloutBehavior?.preferOneByOne) ||
        isDefined(workloadOverrides?.rolloutBehavior?.type) ||
        isDefined(workloadOverrides?.rolloutBehavior?.delaySeconds),
    }),
    optimizedResources: ({
      workload: {
        workloadConfigV2: {
          vpaConfig: { cpu, memory },
        },
        workloadOverrides,
      },
    }) => ({
      cpu: isWorkloadResourceOptimized(cpu),
      memory: isWorkloadResourceOptimized(memory),
      isOverridden: isDefined(
        workloadOverrides?.vertical?.cpu?.managementOption,
        workloadOverrides?.vertical?.memory?.managementOption
      ),
    }),

    excludedContainers: ({
      workload: {
        workloadConfigV2: {
          vpaConfig: { excludedContainers },
        },
        workloadOverrides,
      },
    }) => ({
      enabled: !!excludedContainers?.length,
      containers: excludedContainers || [],
      isOverridden:
        isDefined(workloadOverrides?.vertical?.excludedContainers) &&
        !!workloadOverrides?.vertical?.excludedContainers?.length,
    }),

    optimizationThreshold: ({
      workload: {
        workloadConfigV2: {
          vpaConfig: { cpu, memory },
        },
        workloadOverrides,
      },
    }) => ({
      cpu: getOptimizationThresholdFormModel(cpu.applyThresholdStrategy),
      memory: getOptimizationThresholdFormModel(memory.applyThresholdStrategy),
      isOverridden: isDefined(
        workloadOverrides?.vertical?.cpu?.applyThresholdStrategy,
        workloadOverrides?.vertical?.memory?.applyThresholdStrategy
      ),
    }),

    downscaling: ({ workload }) => {
      const {
        workloadConfigV2: {
          vpaConfig: { downscaling },
        },
        workloadOverrides,
      } = workload;

      const applyType =
        apiToVerticalRightsizingPolicyFormModelFunctions.applyType({
          workload,
        });

      return {
        value: !downscaling?.applyType
          ? 'IMMEDIATE'
          : downscaling.applyType === 'UNKNOWN'
          ? applyType.value
          : downscaling.applyType,
        isOverridden: isDefined(workloadOverrides?.vertical?.downscaling),
      };
    },

    antiAffinity: ({
      workload: {
        workloadConfigV2: {
          vpaConfig: { antiAffinity },
        },
        workloadOverrides,
      },
    }): { value: PolicyAutoscalerFormMode; isOverridden: boolean } => ({
      value: getAntiAffinity({ antiAffinity }),
      isOverridden: isDefined(workloadOverrides?.vertical?.antiAffinity),
    }),

    overhead: ({
      workload: {
        workloadConfigV2: {
          vpaConfig: { cpu, memory },
        },
        workloadOverrides,
      },
    }) => ({
      cpu: getOverhead(cpu),
      memory: getOverhead(memory),
      isOverridden: isDefined(
        workloadOverrides?.vertical?.cpu?.overhead,
        workloadOverrides?.vertical?.memory?.overhead
      ),
    }),
    memoryGrowthDetection: ({
      workload: {
        workloadConfigV2: {
          vpaConfig: { anomalyDetection },
        },
        workloadOverrides,
      },
    }) => ({
      enabled: anomalyDetection?.infiniteMemoryScaling?.enabled ?? true,
      isOverridden: isDefined(
        workloadOverrides?.vertical?.anomalyDetection?.infiniteMemoryScaling
      ),
    }),
    stallDetection: ({
      workload: {
        workloadConfigV2: {
          vpaConfig: { anomalyDetection },
        },
        workloadOverrides,
      },
    }) => ({
      ...getStallDetectionFormModel(anomalyDetection),
      isOverridden: isDefined(
        workloadOverrides?.vertical?.anomalyDetection?.cpuPressure
          ?.cpuStallThresholdPercentage,
        workloadOverrides?.vertical?.anomalyDetection?.cpuPressure
          ?.minPressuredPodPercentage
      ),
    }),

    recommendationPercentile: ({
      workload: {
        workloadConfigV2: {
          vpaConfig: { cpu, memory },
        },
        workloadOverrides,
      },
    }) => ({
      cpu: getRecommendationPercentile(cpu),
      memory: getRecommendationPercentile(memory),
      isOverridden:
        // isOverridden if function is defined or args are not empty
        isDefined(
          workloadOverrides?.vertical?.cpu?.function,
          workloadOverrides?.vertical?.memory?.function
        ) ||
        !!workloadOverrides?.vertical?.cpu?.args?.length ||
        !!workloadOverrides?.vertical?.memory?.args?.length,
    }),

    resourceLimits: ({
      workload: {
        workloadConfigV2: {
          vpaConfig: { cpu, memory },
        },
        workloadOverrides,
      },
    }) => ({
      cpu: getResourceLimitsCpu(cpu.limit),
      memory: getResourceLimitsMemory(memory.limit),
      isOverridden: isDefined(
        workloadOverrides?.vertical?.cpu?.limit,
        workloadOverrides?.vertical?.memory?.limit
      ),
    }),

    lookbackPeriod: ({
      workload: {
        workloadConfigV2: {
          vpaConfig: { cpu, memory },
        },
        workloadOverrides,
      },
    }) => ({
      cpu: getLookbackDaysHours(cpu),
      memory: getLookbackDaysHours(memory),
      isOverridden: isDefined(
        workloadOverrides?.vertical?.cpu?.lookBackPeriodSeconds,
        workloadOverrides?.vertical?.memory?.lookBackPeriodSeconds
      ),
    }),

    startupMetrics: ({
      workload: {
        workloadConfigV2: {
          vpaConfig: { startup },
        },
        workloadOverrides,
      },
    }) => ({
      ...secondsToStartupMetricsFormModel(startup),
      isOverridden: isDefined(
        workloadOverrides?.vertical?.startup?.periodSeconds
      ),
    }),

    memoryEvent: ({ workload }) => {
      const applyType =
        apiToVerticalRightsizingPolicyFormModelFunctions.applyType({
          workload,
        });

      const {
        workloadConfigV2: {
          vpaConfig: { memoryEvent },
        },
        workloadOverrides,
      } = workload;

      return {
        value: !memoryEvent?.applyType
          ? 'DEFERRED'
          : memoryEvent.applyType === 'UNKNOWN'
          ? applyType.value
          : memoryEvent.applyType,
        isOverridden: isDefined(workloadOverrides?.vertical?.memoryEvent),
      };
    },

    customMetrics: ({ workload }) => {
      return workload.containers.map(({ name: containerName }) => {
        const containerConfig =
          workload.workloadConfigV2.vpaConfig.containers?.find(
            (c) => c.containerName === containerName
          );
        const containerOverrides =
          workload.workloadOverrides?.vertical?.containers?.find(
            (c) => c.containerName === containerName
          );
        return {
          containerName,
          jvmMemoryOptimization: containerConfig?.jvm?.enabled ?? false,
          jvmAutoInstrument: containerConfig?.jvm?.autoInstrument ?? false,
          isOverridden: containerOverrides?.jvm !== undefined,
        };
      });
    },

    workloadConstraints: ({ workload }) => {
      return workload.containers.map(({ name: containerName }) => {
        const containerConstraints =
          workload.workloadConfigV2.vpaConfig.containerConstraints?.find(
            (c) => c.containerName === containerName
          );
        const containerConstraintsOverridden =
          workload.workloadOverrides?.vertical?.containerConstraints?.find(
            (c) => c.containerName === containerName
          );

        // container is not set, get default values
        if (!containerConstraints) {
          return {
            containerName,
            ...getWorkloadConstraints(workload.workloadConfigV2.vpaConfig),
            isOverridden: !!containerConstraintsOverridden,
          };
        }
        return {
          containerName,
          ...getWorkloadConstraints(containerConstraints),
          isOverridden: !!containerConstraintsOverridden,
        };
      });
    },
    hpaConverter: ({
      workload: {
        workloadConfigV2: { vpaConfig },
        workloadOverrides,
      },
    }) => ({
      enabled: !!vpaConfig.hpaConverters?.length,
      isOverridden: isDefined(workloadOverrides?.vertical?.hpaConverters),
    }),
    predictiveScaling: ({
      workload: {
        workloadConfigV2: { vpaConfig },
        workloadOverrides,
        predictionInsights,
      },
    }) => ({
      cpu: {
        // Ineligible workloads stay disabled even when the saved config is on.
        enabled:
          !!predictionInsights?.isEligible &&
          !!vpaConfig.predictiveScaling?.cpu?.enabled,
      },
      isOverridden: isDefined(workloadOverrides?.vertical?.predictiveScaling),
    }),
  };

export const apiToVerticalRightsizingPolicyFormModel = ({
  ...props
}: VerticalRightsizingPolicyFormModelGeneratorParams): VerticalRightsizingPolicyFormModel => {
  const keys = Object.keys(
    apiToVerticalRightsizingPolicyFormModelFunctions
  ) as (keyof VerticalRightsizingPolicyFormModelGenerators)[];

  return keys.reduce((acc, key) => {
    return {
      ...acc,
      [key]: apiToVerticalRightsizingPolicyFormModelFunctions[key]({
        ...props,
      }),
    };
  }, {} as VerticalRightsizingPolicyFormModel);
};

export const apiToFormModel = (
  workload: PreparedWorkload
): WorkloadDetailsFormModel<HorizontalRightsizingFormModel> => {
  const {
    workloadConfigV2: { hpaConfig },
  } = workload;

  const horizontalRightsizing = {
    enabled: hpaConfig.managementOption === WorkloadOptimizationState.MANAGED,
    pods: {
      min: hpaConfig.minReplicas ?? null,
      max: hpaConfig.maxReplicas ?? null,
    },
  } satisfies HorizontalRightsizingFormModel;

  return {
    horizontalRightsizing,
    verticalRightsizing: {
      policy: apiToVerticalRightsizingPolicyFormModel({
        workload,
      }),
    },
  };
};

export const apiToFormModelV2 = (
  workload: PreparedWorkload,
  hpaStatus: HpaV2Status
): WorkloadDetailsFormModel<HorizontalRightsizingFormModelV2> => {
  const {
    workloadConfigV2: { hpaConfig },
  } = workload;

  const { minReplicas, maxReplicas, metrics, behavior } = hpaConfig;

  const horizontalRightsizing = {
    enabled: hpaConfig.managementOption === WorkloadOptimizationState.MANAGED,
    hpaSettings:
      // Typecast is needed because we know that hasHpaSettings properties are not undefined after hasHpaSettings check
      hpaStatus === 'available' &&
      workloadHasHpaV2Settings(workload) &&
      !isHpaV2Unavailable(workload)
        ? {
            basicBehavior: {
              minReplicas: minReplicas!,
              maxReplicas: maxReplicas!,
            },
            metrics: mapApiMetricsToFormModel(metrics!),
            autoscalerBehavior: mapApiAutoscalerBehaviorToFormModel(behavior),
          }
        : undefined,
    isOverridden:
      isDefined(
        workload.workloadOverrides?.horizontal?.minReplicas,
        workload.workloadOverrides?.horizontal?.maxReplicas
      ) ||
      (isDefined(workload.workloadOverrides?.horizontal?.metrics) &&
        // We agreed with BE that [] will be treated as no metrics override as well
        // https://discord.com/channels/682675309476053199/1435268584619118673/1441363944211087414
        !!workload.workloadOverrides?.horizontal?.metrics?.length) ||
      isDefined(workload.workloadOverrides?.horizontal?.behavior),
  } satisfies HorizontalRightsizingFormModelV2;

  return {
    horizontalRightsizing,
    verticalRightsizing: {
      policy: apiToVerticalRightsizingPolicyFormModel({
        workload,
      }),
    },
  };
};

type FormModelToApiArgs<T> = {
  model: WorkloadDetailsFormModel<T>;
  workload: PreparedWorkload;
  dirtyFields: Record<string, boolean>;
  hpaV2Status: HpaV2Status;
};

export function fieldChanged<HPA>(
  fieldPath: Path<WorkloadDetailsFormModel<HPA>>,
  dirtyFields: Record<string, boolean>
): boolean {
  return Object.keys(dirtyFields).some((key) => key.includes(fieldPath));
}

export const getVpaConfig = ({
  verticalRightsizing,
  dirtyFields,
}: {
  verticalRightsizing: WorkloadDetailsFormModel['verticalRightsizing'];
  dirtyFields: Record<string, boolean>;
}): UpdateVerticalWorkloadScaling | null => {
  const model = verticalRightsizing.policy;

  const result: UpdateVerticalWorkloadScaling = {};

  if (fieldChanged('verticalRightsizing.policy.antiAffinity', dirtyFields)) {
    result.antiAffinity = model.antiAffinity.isOverridden
      ? getAntiAffinityApiValue(model.antiAffinity.value)
      : { considerAntiAffinity: null };
  }

  const constraintsChanged = fieldChanged(
    'verticalRightsizing.policy.workloadConstraints',
    dirtyFields
  );
  const customMetricsChanged = fieldChanged(
    'verticalRightsizing.policy.customMetrics',
    dirtyFields
  );

  if (constraintsChanged || customMetricsChanged) {
    // Always include ALL currently-overridden containers from both fields.
    // containerConfig is replaced on every PATCH, so omitting an already-overridden
    // container from the other field would silently clear it on the API side.
    const constraintEntries = model.workloadConstraints
      .filter((c) => c.isOverridden)
      .map(({ containerName, cpu, memory }) => ({
        containerName,
        cpu: getConstraintsApiValues(cpu),
        memory: getConstraintsApiValues(memory, toMibOrNull),
      }));

    const jvmEntries = model.customMetrics
      .filter((c) => c.isOverridden)
      .map(({ containerName, jvmMemoryOptimization, jvmAutoInstrument }) => ({
        containerName,
        jvm: {
          enabled: jvmMemoryOptimization,
          autoInstrument: jvmAutoInstrument,
        },
      }));

    const allNames = new Set([
      ...constraintEntries.map((c) => c.containerName),
      ...jvmEntries.map((c) => c.containerName),
    ]);

    result.containerConfig = Array.from(allNames).map((containerName) => {
      const constraints = constraintEntries.find(
        (c) => c.containerName === containerName
      );
      const jvm = jvmEntries.find((c) => c.containerName === containerName);
      return {
        containerName,
        ...constraints,
        ...jvm,
      };
    });
  }

  if (Object.keys(result).length) {
    return result;
  }

  return null;
};

export const getHpaConfig = ({
  dirtyFields,
  horizontalRightsizing,
}: {
  horizontalRightsizing: HorizontalRightsizingFormModel;
  dirtyFields: Record<string, boolean>;
}): UpdateHorizontalWorkloadScaling | null => {
  const result: UpdateHorizontalWorkloadScaling = {};

  const newPods = horizontalRightsizing.pods;

  if (
    fieldChanged<HorizontalRightsizingFormModel>(
      'horizontalRightsizing.enabled',
      dirtyFields
    )
  ) {
    result.managementOption = horizontalRightsizing.enabled
      ? WorkloadOptimizationState.MANAGED
      : WorkloadOptimizationState.READ_ONLY;

    if (horizontalRightsizing.enabled) {
      result.minReplicas = newPods.min;
      result.maxReplicas = newPods.max;
    } else {
      result.minReplicas = null;
      result.maxReplicas = null;
    }

    return result;
  }

  // If management option didn't change, check min/max replicas

  if (
    fieldChanged<HorizontalRightsizingFormModel>(
      'horizontalRightsizing.pods.min',
      dirtyFields
    )
  ) {
    result.minReplicas = newPods.min;
  }

  if (
    fieldChanged<HorizontalRightsizingFormModel>(
      'horizontalRightsizing.pods.max',
      dirtyFields
    )
  ) {
    result.maxReplicas = newPods.max;
  }

  if (Object.keys(result).length) {
    return result;
  }

  return null;
};

export const getHpaConfigV2 = ({
  dirtyFields,
  horizontalRightsizing,
}: {
  horizontalRightsizing: HorizontalRightsizingFormModelV2;
  dirtyFields: Record<string, boolean>;
}): UpdateHorizontalWorkloadScaling | null => {
  const result: UpdateHorizontalWorkloadScaling = {};

  if (
    horizontalRightsizing.hpaSettings !== undefined &&
    fieldChanged<HorizontalRightsizingFormModelV2>(
      'horizontalRightsizing',
      dirtyFields
    )
  ) {
    const {
      hpaSettings: {
        basicBehavior: { minReplicas, maxReplicas },
        metrics,
        autoscalerBehavior,
      },
      isOverridden,
    } = horizontalRightsizing;

    result.minReplicas = isOverridden ? minReplicas : null;
    result.maxReplicas = isOverridden ? maxReplicas : null;

    result.metrics = isOverridden
      ? mapFormModelMetricsToApi(metrics)
      : undefined; // todo: HPAv2 send null after backend merge

    result.behavior = isOverridden
      ? mapFormModelAutoscalerBehaviorToApi(autoscalerBehavior)
      : undefined;

    result.useNative = isOverridden ? true : null;
  }

  if (Object.keys(result).length) {
    return result;
  }

  return null;
};

export const formModelToApi = ({
  workload,
  model: { horizontalRightsizing, verticalRightsizing },
  dirtyFields,
  hpaV2Status,
}: FormModelToApiArgs<HorizontalRightsizingFormModel>): PartialWorkloadUpdateRequest => {
  const result: PartialWorkloadUpdateRequest = {
    clusterId: workload.clusterId,
    workloadId: workload.id,
    workload: {
      workloadConfig: {},
    },
  };

  if (hpaV2Status !== 'outdated') {
    const hpaConfig = getHpaConfig({
      horizontalRightsizing,
      dirtyFields,
    });

    if (hpaConfig) {
      result.workload = {
        ...result.workload,
        workloadConfig: {
          ...result.workload?.workloadConfig,
          hpaConfig,
        },
      };
    }
  }

  const vpaConfig = getVpaConfig({ verticalRightsizing, dirtyFields });
  if (vpaConfig) {
    result.workload = {
      ...result.workload,
      workloadConfig: {
        ...result.workload?.workloadConfig,
        vpaConfig,
      },
    };
  }

  return result;
};

export const formModelToApiV2 = ({
  workload,
  model: { horizontalRightsizing, verticalRightsizing },
  dirtyFields,
  hpaV2Status,
}: FormModelToApiArgs<HorizontalRightsizingFormModelV2>): PartialWorkloadUpdateRequest => {
  const result: PartialWorkloadUpdateRequest = {
    clusterId: workload.clusterId,
    workloadId: workload.id,
    workload: {
      workloadConfig: {},
    },
  };

  if (hpaV2Status !== 'outdated') {
    const hpaConfig = getHpaConfigV2({
      horizontalRightsizing,
      dirtyFields,
    });

    if (hpaConfig) {
      result.workload = {
        ...result.workload,
        workloadConfig: {
          ...result.workload?.workloadConfig,
          hpaConfig,
        },
      };
    }
  }

  const vpaConfig = getVpaConfig({ verticalRightsizing, dirtyFields });
  if (vpaConfig) {
    result.workload = {
      ...result.workload,
      workloadConfig: {
        ...result.workload?.workloadConfig,
        vpaConfig,
      },
    };
  }

  return result;
};
